import { client } from './amplifyClient';

/**
 * Location Management Service
 * CRUD operations for user locations with primary location handling
 */

export interface CreateLocationInput {
  userId: string;
  name: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  country?: string;
  city?: string;
  elevation?: number;
  nearWater?: boolean;
  waterType?: string;
  hasUnobstructedHorizon?: boolean;
  horizonDirection?: string;
  placeId?: string;
  formattedAddress?: string;
  region?: string;
  notificationsEnabled?: boolean;
  notes?: string;
  imageUrl?: string;
}

export interface UpdateLocationInput {
  name?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  country?: string;
  city?: string;
  elevation?: number;
  nearWater?: boolean;
  waterType?: string;
  hasUnobstructedHorizon?: boolean;
  horizonDirection?: string;
  placeId?: string;
  formattedAddress?: string;
  region?: string;
  notificationsEnabled?: boolean;
  notes?: string;
  imageUrl?: string;
  isPrimary?: boolean;
}

export class LocationServiceError extends Error {
  constructor(
    message: string,
    public code?: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'LocationServiceError';
  }
}

/**
 * Get all locations for a user
 * Returns locations sorted by creation date (newest first)
 */
export async function getUserLocations(userId: string) {
  try {
    const { data, errors } = await client.models.Location.list({
      filter: { userId: { eq: userId } },
    });

    if (errors && errors.length > 0) {
      throw new LocationServiceError(
        'Failed to fetch user locations',
        'FETCH_ERROR',
        errors
      );
    }

    // Sort by isPrimary first, then by createdAt
    const sorted = (data || []).sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;

      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bDate - aDate;
    });

    return sorted;
  } catch (error) {
    if (error instanceof LocationServiceError) {
      throw error;
    }
    throw new LocationServiceError(
      'Unexpected error fetching locations',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Get primary location for a user
 * Returns null if no primary location is set
 */
export async function getPrimaryLocation(userId: string) {
  try {
    const locations = await getUserLocations(userId);
    return locations.find(loc => loc.isPrimary) || null;
  } catch (error) {
    throw new LocationServiceError(
      'Failed to get primary location',
      'FETCH_ERROR',
      error
    );
  }
}

/**
 * Create a new location
 * If this is the user's first location, it will be set as primary
 */
export async function createLocation(input: CreateLocationInput) {
  try {
    // Check if user has any existing locations
    const existingLocations = await getUserLocations(input.userId);
    const isFirstLocation = existingLocations.length === 0;

    const { data, errors } = await client.models.Location.create({
      userId: input.userId,
      name: input.name,
      latitude: input.latitude,
      longitude: input.longitude,
      isPrimary: isFirstLocation, // Auto-set first location as primary
      timezone: input.timezone,
      country: input.country,
      city: input.city,
      elevation: input.elevation,
      nearWater: input.nearWater ?? false,
      waterType: input.waterType,
      hasUnobstructedHorizon: input.hasUnobstructedHorizon ?? false,
      horizonDirection: input.horizonDirection,
      placeId: input.placeId,
      formattedAddress: input.formattedAddress,
      region: input.region,
      notificationsEnabled: input.notificationsEnabled ?? true,
      notes: input.notes,
      imageUrl: input.imageUrl,
      createdAt: new Date().toISOString(),
    });

    if (errors && errors.length > 0) {
      throw new LocationServiceError(
        'Failed to create location',
        'CREATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new LocationServiceError(
        'No data returned after creating location',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof LocationServiceError) {
      throw error;
    }
    throw new LocationServiceError(
      'Unexpected error creating location',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Update an existing location
 * Performs partial updates
 */
export async function updateLocation(id: string, updates: UpdateLocationInput) {
  try {
    const { data, errors } = await client.models.Location.update({
      id,
      ...updates,
    });

    if (errors && errors.length > 0) {
      throw new LocationServiceError(
        'Failed to update location',
        'UPDATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new LocationServiceError(
        'No data returned after updating location',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof LocationServiceError) {
      throw error;
    }
    throw new LocationServiceError(
      'Unexpected error updating location',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Delete a location
 * If deleting the primary location, automatically sets another location as primary
 */
export async function deleteLocation(id: string, userId: string) {
  try {
    // Get the location to check if it's primary
    const locations = await getUserLocations(userId);
    const locationToDelete = locations.find(loc => loc.id === id);

    if (!locationToDelete) {
      throw new LocationServiceError(
        'Location not found',
        'NOT_FOUND'
      );
    }

    const { data, errors } = await client.models.Location.delete({ id });

    if (errors && errors.length > 0) {
      throw new LocationServiceError(
        'Failed to delete location',
        'DELETE_ERROR',
        errors
      );
    }

    // If we deleted the primary location and there are other locations,
    // set the next location as primary
    if (locationToDelete.isPrimary && locations.length > 1) {
      const nextLocation = locations.find(loc => loc.id !== id);
      if (nextLocation) {
        await setPrimaryLocation(nextLocation.id, userId);
      }
    }

    return data;
  } catch (error) {
    if (error instanceof LocationServiceError) {
      throw error;
    }
    throw new LocationServiceError(
      'Unexpected error deleting location',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Set a location as primary
 * Automatically unsets the previous primary location
 */
export async function setPrimaryLocation(locationId: string, userId: string) {
  try {
    // Get all user locations
    const locations = await getUserLocations(userId);
    const targetLocation = locations.find(loc => loc.id === locationId);

    if (!targetLocation) {
      throw new LocationServiceError(
        'Location not found',
        'NOT_FOUND'
      );
    }

    // If already primary, nothing to do
    if (targetLocation.isPrimary) {
      return targetLocation;
    }

    // Unset current primary location
    const currentPrimary = locations.find(loc => loc.isPrimary);
    if (currentPrimary) {
      await client.models.Location.update({
        id: currentPrimary.id,
        isPrimary: false,
      });
    }

    // Set new primary location
    const { data, errors } = await client.models.Location.update({
      id: locationId,
      isPrimary: true,
    });

    if (errors && errors.length > 0) {
      throw new LocationServiceError(
        'Failed to set primary location',
        'UPDATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new LocationServiceError(
        'No data returned after setting primary location',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof LocationServiceError) {
      throw error;
    }
    throw new LocationServiceError(
      'Unexpected error setting primary location',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Get location by ID
 */
export async function getLocationById(id: string) {
  try {
    const { data, errors } = await client.models.Location.get({ id });

    if (errors && errors.length > 0) {
      throw new LocationServiceError(
        'Failed to fetch location',
        'FETCH_ERROR',
        errors
      );
    }

    return data;
  } catch (error) {
    if (error instanceof LocationServiceError) {
      throw error;
    }
    throw new LocationServiceError(
      'Unexpected error fetching location',
      'UNKNOWN_ERROR',
      error
    );
  }
}
